// /translate/$language/$doctype — 270 high-intent landing pages
// (18 languages × 15 document types)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { TRANSLATION_LANGUAGES, getTranslationLanguage } from "@/data/translation-languages";
import { TRANSLATION_DOCTYPES, getTranslationDocType } from "@/data/translation-doctypes";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema } from "@/lib/aeo";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { dynamicOgImage, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import {
  FileCheck2, ShieldCheck, BadgeCheck, Clock, Globe2, Phone, MessageCircle, Languages, ArrowRight,
} from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/translate/$language/$doctype/")({
  loader: ({ params }) => {
    const l = getTranslationLanguage(params.language);
    const d = getTranslationDocType(params.doctype);
    if (!l || !d) throw notFound();
    return { lang: params.language, doc: params.doctype };
  },
  head: ({ params }) => {
    const l = getTranslationLanguage(params.language);
    const d = getTranslationDocType(params.doctype);
    if (!l || !d) return { meta: [{ title: ctaTitle("ไม่พบหน้านี้", "/translate/$language/$doctype", "th")}] };
    const url = `${SITE}/translate/${l.slug}/${d.slug}`;
    const price = l.priceFromTh + d.surcharge;
    const ratingKey = `translate/${l.slug}/${d.slug}`;
    const rating = aggregateRatingNode(ratingKey);
    const reviews = reviewNodes(ratingKey, `${url}#service`, 3);
    const title = `แปล${d.nameTh} เป็น${l.nameTh} รับรอง MoJ + MFA — เริ่ม ฿${price.toLocaleString()} | NYC`;
    const desc = `บริการแปล${d.nameTh} (${d.nameEn}) เป็น${l.nameTh} โดยนักแปลขึ้นทะเบียน ใช้ยื่นได้ทันที — ${d.destinationUses.slice(0, 3).join(", ")} · เสร็จ ${l.turnaround} — ส่งภาพเอกสารให้เราตรวจก่อน ฟรี`;
    const allKw = [...d.keywordsTh, ...l.keywords, `แปล${d.nameTh}${l.nameTh}`];
    return {
      meta: [
        { title: ctaTitle(title, "/translate/$language/$doctype", "th") },
        { name: "description", content: ctaDesc(desc, "/translate/$language/$doctype", "th")},
        { name: "keywords", content: allKw.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/translate/$language/$doctype", "th")},
        { property: "og:description", content: ctaDesc(desc, "/translate/$language/$doctype", "th")},
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "th_TH" },
        { property: "og:image", content: dynamicOgImage({ title: `แปล${d.nameTh} เป็น${l.nameTh}`, subtitle: `รับรอง MoJ + MFA · เริ่ม ฿${price.toLocaleString()} · ${l.turnaround}`, badge: `${d.nameEn.toUpperCase()} → ${l.nameEn.toUpperCase()}` }) },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
        { rel: "alternate", hrefLang: "en", href: url.replace(SITE, `${SITE}/en`) },
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `แปล${d.nameTh}เป็น${l.nameTh}`,
              serviceType: `Certified translation of ${d.nameEn} into ${l.nameEn}`,
              areaServed: { "@type": "Country", name: "Thailand" },
              availableLanguage: [
                { "@type": "Language", name: "Thai", iso6391: "th" },
                { "@type": "Language", name: l.nameEn, alternateName: l.nativeName, iso6391: l.iso639 },
              ],
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
              },
              offers: {
                "@type": "Offer",
                price,
                priceCurrency: "THB",
                priceSpecification: {
                  "@type": "UnitPriceSpecification",
                  price,
                  priceCurrency: "THB",
                  unitText: "page",
                },
                availability: "https://schema.org/InStock",
              },
              aggregateRating: rating,
            }),
          },
          ...reviews.map((rv) => ({
            type: "application/ld+json" as const,
            children: JSON.stringify(rv),
          })),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "@id": `${url}#howto`,
              name: `วิธีสั่งแปล${d.nameTh}เป็น${l.nameTh}`,
              totalTime: "PT30M",
              step: [
                { "@type": "HowToStep", position: 1, name: "ส่งไฟล์เอกสาร", text: `ถ่ายรูปหรือสแกน${d.nameTh} ส่งผ่าน LINE @nycli — เราตอบกลับใน 15 นาที` },
                { "@type": "HowToStep", position: 2, name: "รับใบเสนอราคา", text: `ราคา ฿${price.toLocaleString()}/หน้า — ${d.pagesEstimate}` },
                { "@type": "HowToStep", position: 3, name: "ชำระมัดจำ 50%", text: "โอนเงิน/PromptPay แล้วส่งสลิป" },
                { "@type": "HowToStep", position: 4, name: "นักแปลทำงาน", text: `นักแปล${l.nameTh}ขึ้นทะเบียนเริ่มงานทันที — QA 2 รอบ` },
                { "@type": "HowToStep", position: 5, name: "ขั้นตอนรับรอง", text: `รับรองตามลำดับ: ${d.certifications.join(" → ")}` },
                { "@type": "HowToStep", position: 6, name: "ส่งงาน", text: `รับ PDF ภายใน ${l.turnaround} + ฉบับจริงส่ง EMS` },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: [...d.faqExtra, ...l.faqs.slice(0, 2)].map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "แปลรายภาษา", item: `${SITE}/translate` },
                { "@type": "ListItem", position: 3, name: `แปล${l.nameTh}`, item: `${SITE}/translate/${l.slug}` },
                { "@type": "ListItem", position: 4, name: d.nameTh, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: TranslatePairPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบหน้านี้</h1>
      <Link to="/translate" className="mt-4 inline-block text-gold underline">
        ไปหน้าแปลรายภาษา
      </Link>
    </main>
  ),
});

function TranslatePairPage() {
  const { lang, doc } = Route.useLoaderData() as { lang: string; doc: string };
  const l = getTranslationLanguage(lang)!;
  const d = getTranslationDocType(doc)!;
  const price = l.priceFromTh + d.surcharge;
  const otherDocs = TRANSLATION_DOCTYPES.filter((x) => x.slug !== doc).slice(0, 8);
  const otherLangs = TRANSLATION_LANGUAGES.filter((x) => x.slug !== lang).slice(0, 8);
  const faqs = [...d.faqExtra, ...l.faqs.slice(0, 2)];

  return (
    <main className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/translate" className="hover:text-gold">แปลรายภาษา</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/translate/$language" params={{ language: l.slug }} className="hover:text-gold">{l.nameTh}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{d.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Languages className="h-3.5 w-3.5" /> {d.nameEn} → {l.nameEn}{l.nativeName ? ` · ${l.nativeName}` : ""}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            แปล{d.nameTh} เป็น{l.nameTh} <span className="text-gold">รับรองครบทุกขั้นตอน</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{d.description}</p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ราคาเริ่ม", v: `฿${price.toLocaleString()}/หน้า` },
              { k: "ขนาดทั่วไป", v: d.pagesEstimate },
              { k: "เสร็จภายใน", v: l.turnaround },
              { k: "Rating", v: "2,147+ เคสสำเร็จ" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5">
              <MessageCircle className="h-4 w-4" /> ส่งไฟล์ผ่าน LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        {/* Quotable AEO */}
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service (DBD 0435567000061, ตั้งแต่ปี 2010) แปล{d.nameTh} ({d.nameEn}) เป็น{l.nameTh} โดยนักแปลขึ้นทะเบียน MoJ — เริ่ม ฿{price.toLocaleString()}/หน้า เสร็จ {l.turnaround} รับรองครบ {d.certifications.join(" → ")} ใช้ยื่น {d.destinationUses.slice(0, 3).join(", ")} ได้ทันที บริการสำเร็จ 2,147+ เคส
        </blockquote>

        {/* Certification chain */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ขั้นตอนรับรองครบวงจร</h2>
          <p className="mt-2 text-sm text-slate-600">การแปล{d.nameTh}ต้องผ่านห่วงโซ่รับรองดังนี้ — เราดำเนินการให้ครบทุกขั้นตอนในที่เดียว</p>
          <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {d.certifications.map((c, i) => (
              <li key={c} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Step {i + 1}</div>
                <div className="mt-1 font-display text-base font-extrabold text-navy-deep">{c}</div>
                <ShieldCheck className="mt-2 h-4 w-4 text-emerald-500" />
              </li>
            ))}
          </ol>
        </section>

        {/* Destination uses */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">วัตถุประสงค์การใช้งานที่พบบ่อย</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {d.destinationUses.map((u) => (
              <li key={u} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {u}
              </li>
            ))}
          </ul>
        </section>

        {/* Country coverage */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ประเทศปลายทางที่ยอมรับ</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {l.countryOfUse.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-navy-deep/15 bg-white px-3.5 py-1.5 text-[13px] font-semibold text-navy-deep">
                <Globe2 className="h-3.5 w-3.5 text-amber-600" /> {c}
              </span>
            ))}
          </div>
        </section>

        {/* Pricing */}
        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคาเริ่มต้น</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            แปล{d.nameTh} → {l.nameTh} ฿{price.toLocaleString()}/หน้า
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ตราประทับ + ลายเซ็นนักแปลขึ้นทะเบียน MoJ</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Notary Public รับรองได้ +฿1,500/ฉบับ</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รับรอง MFA (กงสุล) ครบจบในที่เดียว</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ส่ง EMS ฉบับจริงทั่วประเทศ</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        {/* FAQ */}
        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={faqs} />
          </div>
        </section>

        {/* Cross-links: other docs in this language */}
        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            เอกสารอื่นที่แปลเป็น{l.nameTh}
          </h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {otherDocs.map((o) => (
              <Link
                key={o.slug}
                to="/translate/$language/$doctype"
                params={{ language: l.slug, doctype: o.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{o.nameEn}</div>
                <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                  แปล{o.nameTh}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  เริ่ม ฿{(l.priceFromTh + o.surcharge).toLocaleString()} · {o.pagesEstimate}
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Cross-links: same doc in other languages */}
        <section className="mt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            แปล{d.nameTh}เป็นภาษาอื่น
          </h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {otherLangs.map((o) => (
              <Link
                key={o.slug}
                to="/translate/$language/$doctype"
                params={{ language: o.slug, doctype: d.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{o.nameEn}{o.nativeName ? ` · ${o.nativeName}` : ""}</div>
                <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600 flex items-center gap-1">
                  {d.nameTh} <ArrowRight className="h-3 w-3" /> {o.nameTh}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">เริ่ม ฿{(o.priceFromTh + d.surcharge).toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
